// Client for the existing Homeland Stay PHP booking backend
// (the production codebase in /Users/varunjani/Downloads/Homeland — api/csrf.php,
// api/bookings.php?action=calendar&public=true, api/public_booking.php).
// In dev, Vite proxies /api to that backend (see vite.config.ts).

export interface CalendarBooking {
  check_in_date: string;
  check_out_date: string;
  status: string;
}

export interface CalendarData {
  bookings: CalendarBooking[];
  serverToday: string;
}

export interface DateRange {
  checkIn: string;
  checkOut: string;
  days: number;
}

export interface AvailabilityResult {
  available: boolean;
  reason?: string;
  nextAvailable?: DateRange | null;
  alternatives?: DateRange[];
  requestedDays: number;
}

export type DocType = 'aadhar' | 'pan' | 'passport' | 'license';

export interface BookingFields {
  name: string;
  phone: string;
  email: string;
  stateCity: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  kids: number;
  message: string;
  docNumbers: Partial<Record<DocType, string>>;
  docFiles: Partial<Record<DocType, File>>;
  aadharBackFile?: File | null;
}

export interface BookingResponse {
  success: boolean;
  message?: string;
  booking_reference?: string;
}

import { parseJsonResponse } from './apiFetch';
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

export async function fetchCsrfToken(): Promise<string> {
  const res = await fetch(`${API_BASE}/csrf.php`);
  const data = await parseJsonResponse(res);
  if (!data.success) throw new Error('Failed to fetch CSRF token');
  return data.csrf_token;
}

export async function fetchCalendar(): Promise<CalendarData> {
  const res = await fetch(`${API_BASE}/bookings.php?action=calendar&public=true&v=${Date.now()}`);
  const data = await parseJsonResponse(res);
  if (!data.success) throw new Error('Failed to load availability calendar');
  return {
    // Both Confirmed and Personal (owner-blocked) bookings make dates unavailable
    bookings: (data.bookings as CalendarBooking[]).filter(
      (b) => b.status === 'Confirmed' || b.status === 'Personal',
    ),
    serverToday: data.server_date ?? toDateString(new Date()),
  };
}

export async function submitBooking(fields: BookingFields, csrfToken: string): Promise<BookingResponse> {
  const fd = new FormData();
  fd.append('customer_name', fields.name);
  fd.append('customer_phone', fields.phone);
  fd.append('customer_email', fields.email);
  fd.append('customer_state', fields.stateCity);
  fd.append('check_in_date', fields.checkIn);
  fd.append('check_out_date', fields.checkOut);
  fd.append('num_adults', String(fields.adults));
  fd.append('num_kids', String(fields.kids));
  fd.append('message', fields.message);
  fd.append('csrf_token', csrfToken);

  (['aadhar', 'pan', 'passport', 'license'] as DocType[]).forEach((doc) => {
    const number = fields.docNumbers[doc]?.trim();
    const file = fields.docFiles[doc];
    if (number) fd.append(`${doc}_number`, number);
    if (file) fd.append(`${doc}_file`, file);
  });
  if (fields.aadharBackFile) fd.append('aadhar_back_file', fields.aadharBackFile);

  const res = await fetch(`${API_BASE}/public_booking.php`, { method: 'POST', body: fd });
  return parseJsonResponse(res);
}

/* ---------- Date helpers (all in local time, YYYY-MM-DD strings) ---------- */

export function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toDateString(d);
}

export function nightsBetween(checkIn: string, checkOut: string): number {
  const ms = new Date(`${checkOut}T00:00:00`).getTime() - new Date(`${checkIn}T00:00:00`).getTime();
  return Math.round(ms / 86400000);
}

export function formatDate(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/* ---------- Availability logic (ported from the legacy booking page) ---------- */

// A single date is bookable as a check-in night when no confirmed stay occupies it.
// Same-day turnover is allowed: an existing check-out (guest leaves 10 AM) does not
// block a new check-in (arrives 1 PM).
function isDateAvailable(dateStr: string, bookings: CalendarBooking[]): boolean {
  const checkIns = bookings.some((b) => b.check_in_date === dateStr);
  const checkOuts = bookings.some((b) => b.check_out_date === dateStr);
  const stayOvers = bookings.some((b) => b.check_in_date < dateStr && b.check_out_date > dateStr);

  if (stayOvers) return false;
  if (checkIns && checkOuts) return false;
  if (checkIns) return false;
  return true;
}

function findNextAvailableDates(
  startFromDate: string,
  requestedDays: number,
  bookings: CalendarBooking[],
  today: string,
): DateRange | null {
  let searchDate = startFromDate > today ? startFromDate : today;
  const maxSearchDays = 60;

  for (let i = 0; i < maxSearchDays; i++) {
    if (isDateAvailable(searchDate, bookings)) {
      let consecutive = 0;
      let cursor = searchDate;
      for (let j = 0; j < requestedDays; j++) {
        if (!isDateAvailable(cursor, bookings)) break;
        consecutive++;
        cursor = addDays(cursor, 1);
      }
      if (consecutive === requestedDays) {
        return { checkIn: searchDate, checkOut: addDays(searchDate, requestedDays), days: requestedDays };
      }
    }
    searchDate = addDays(searchDate, 1);
  }
  return null;
}

// Shorter-stay suggestions near the requested check-in (users won't want longer stays).
function findAlternativeOptions(
  requestedCheckIn: string,
  requestedDays: number,
  bookings: CalendarBooking[],
  today: string,
  bestMatch: DateRange | null,
  maxOptions = 2,
): DateRange[] {
  const alternatives: DateRange[] = [];
  let searchDate = addDays(requestedCheckIn, -7);
  if (searchDate < today) searchDate = today;
  const maxSearchDays = 60;

  for (let i = 0; i < maxSearchDays && alternatives.length < maxOptions; i++) {
    if (isDateAvailable(searchDate, bookings)) {
      let consecutive = 0;
      let cursor = searchDate;
      for (let j = 0; j < 30; j++) {
        if (!isDateAvailable(cursor, bookings)) break;
        consecutive++;
        cursor = addDays(cursor, 1);
      }

      if (consecutive >= 1 && consecutive < requestedDays) {
        const shouldSkip =
          (bestMatch !== null && searchDate === bestMatch.checkIn) ||
          alternatives.some((alt) => alt.checkIn === searchDate);
        if (!shouldSkip) {
          alternatives.push({ checkIn: searchDate, checkOut: addDays(searchDate, consecutive), days: consecutive });
        }
      }
      if (consecutive > 0) {
        // Skip ahead to avoid overlapping suggestions
        searchDate = addDays(searchDate, consecutive);
        i += consecutive - 1;
      }
    }
    searchDate = addDays(searchDate, 1);
  }
  return alternatives;
}

export function checkAvailability(
  checkIn: string,
  checkOut: string,
  bookings: CalendarBooking[],
  today: string,
): AvailabilityResult {
  const requestedDays = nightsBetween(checkIn, checkOut);

  const unavailable = (reason: string, fromDate: string): AvailabilityResult => {
    const nextAvailable = findNextAvailableDates(fromDate, requestedDays, bookings, today);
    const alternatives = findAlternativeOptions(checkIn, requestedDays, bookings, today, nextAvailable);
    return { available: false, reason, nextAvailable, alternatives, requestedDays };
  };

  let dateStr = checkIn;
  while (dateStr < checkOut) {
    const hasCheckIn = bookings.some((b) => b.check_in_date === dateStr);
    const hasCheckOut = bookings.some((b) => b.check_out_date === dateStr);
    const hasStayOver = bookings.some((b) => b.check_in_date < dateStr && b.check_out_date > dateStr);

    if (hasStayOver || (hasCheckIn && hasCheckOut)) {
      return unavailable(`The property is fully booked on ${formatDate(dateStr)}.`, dateStr);
    }
    if (hasCheckIn) {
      return unavailable(`The property is not available starting ${formatDate(dateStr)}.`, dateStr);
    }
    dateStr = addDays(dateStr, 1);
  }

  return { available: true, requestedDays };
}
